import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Error } from '@cognitiv/cicada-ui';
import { useAppSelector } from 'ducks/hooks';
import { selectParameter } from 'ducks/parameter/selectors';
import { ProjectParametersFormPayload } from 'ducks/types/payload-types';
import { useValidate } from 'hooks/useValidation';
import { default_parameters, parameter_validation } from 'modals/parameters/constants';

interface ManageParameterContextType {
  form: ProjectParametersFormPayload;
  errors: Error[];
  setForm: Dispatch<SetStateAction<ProjectParametersFormPayload>>;
  onChange: (item: Partial<ProjectParametersFormPayload>) => void;
  validate: () => boolean;
}

interface JobDefinitionProviderProps {
  children: React.ReactNode;
}

export const ManageParameterContext = createContext<ManageParameterContextType>({} as ManageParameterContextType);

export function useManageParameterContext() {
  return useContext(ManageParameterContext);
}

export const ManageParameterContextProvider = ({ children }: JobDefinitionProviderProps) => {
  const parameter = useAppSelector(selectParameter);

  const [form, setForm] = useState<ProjectParametersFormPayload>({ ...default_parameters, ...parameter });
  const { errors, validate, resetFieldError } = useValidate(form, parameter_validation);

  useEffect(() => {
    setForm({ ...default_parameters, ...parameter });
  }, [parameter]);

  const onChange = useCallback(
    (item: Partial<ProjectParametersFormPayload>) => {
      resetFieldError(item);
      setForm((form) => ({ ...form, ...item }));
    },
    [resetFieldError],
  );

  const value = useMemo(
    () => ({
      form,
      setForm,
      onChange,
      errors,
      validate,
    }),
    [form, onChange, errors, validate],
  );

  return (
    <ManageParameterContext.Provider value={value as ManageParameterContextType}>
      {children}
    </ManageParameterContext.Provider>
  );
};
